// ═══════════════ MULTI-VEHICLE BOOKING — CLIENT-SIDE HELPERS (Batch 13) ═══════════════

export const MAX_VEHICLES_PER_GROUP = 4;

// Tier capacities — matches the rider booking wizard tier definitions
export const TIER_CAPACITIES = {
  go: { maxPax: 4, label: 'Reservo Go (Sedan)' },
  comfy: { maxPax: 4, label: 'Reservo Comfy (Premium)' },
  plus: { maxPax: 6, label: 'Reservo Plus (SUV/Van)' },
  vip: { maxPax: 4, label: 'Reservo VIP (Luxury Sedan)' },
  vipPlus: { maxPax: 6, label: 'Reservo VIP+ (Luxury SUV)' },
};

// Compute the smallest single-vehicle option that fits N passengers (for "you could book 1 larger vehicle" hint)
export function suggestSingleVehicleFor(totalPassengers) {
  if (totalPassengers <= 4) return 'go';
  if (totalPassengers <= 6) return 'plus';
  return null;  // 7+ passengers definitely needs multi-vehicle (or a bus)
}

// Status badge styling for vehicle group lifecycle
export function getGroupStatusBadge(status) {
  switch (status) {
    case 'matching':       return { bg: '#fef3c7', color: '#92400e', label: 'Matching drivers...' };
    case 'partial':        return { bg: '#fed7aa', color: '#9a3412', label: 'Partial match' };
    case 'confirmed':      return { bg: 'rgba(45,212,168,0.1)', color: '#059669', label: 'All confirmed' };
    case 'in_progress':    return { bg: '#dbeafe', color: '#1e40af', label: 'In progress' };
    case 'completed':      return { bg: 'rgba(45,212,168,0.1)', color: '#059669', label: 'Completed' };
    case 'cancelled':      return { bg: '#fee2e2', color: '#991b1b', label: 'Cancelled' };
    case 'failed':         return { bg: '#fee2e2', color: '#991b1b', label: 'Failed' };
    default:               return { bg: '#f1f5f9', color: '#5a6578', label: status };
  }
}

// Format vehicles summary: "2x Sedan, 1x SUV"
export function summariseVehicles(vehicles) {
  if (!Array.isArray(vehicles) || vehicles.length === 0) return '';
  const tierCounts = {};
  for (const v of vehicles) {
    const tierLabel = TIER_CAPACITIES[v.tier]?.label?.split(' (')[0] || v.tier;
    tierCounts[tierLabel] = (tierCounts[tierLabel] || 0) + 1;
  }
  return Object.entries(tierCounts).map(([label, count]) => `${count}× ${label}`).join(', ');
}

// Validate that a multi-vehicle booking config is valid before submission
export function validateMultiVehicleConfig(vehicles, totalPassengers) {
  if (!Array.isArray(vehicles)) return { valid: false, error: 'Vehicles must be configured' };
  if (vehicles.length < 2) return { valid: false, error: 'Multi-vehicle bookings require at least 2 vehicles' };
  if (vehicles.length > MAX_VEHICLES_PER_GROUP) return { valid: false, error: `Maximum ${MAX_VEHICLES_PER_GROUP} vehicles per booking` };

  let capacitySum = 0;
  for (let i = 0; i < vehicles.length; i++) {
    const v = vehicles[i];
    if (!v.tier) return { valid: false, error: `Vehicle ${i + 1}: select a service tier` };
    if (!v.passengerCount || v.passengerCount < 1) return { valid: false, error: `Vehicle ${i + 1}: set passenger count` };
    const max = TIER_CAPACITIES[v.tier]?.maxPax || 4;
    if (v.passengerCount > max) {
      return { valid: false, error: `Vehicle ${i + 1}: ${v.passengerCount} passengers exceeds ${TIER_CAPACITIES[v.tier]?.label} capacity (${max})` };
    }
    capacitySum += v.passengerCount;
  }

  if (totalPassengers && capacitySum !== totalPassengers) {
    return { valid: false, error: `Total assigned passengers (${capacitySum}) doesn't match expected (${totalPassengers})` };
  }

  return { valid: true };
}